import { useQuery } from '@tanstack/react-query';

import {
  getPlanAndAssignmentsOptions,
  getUsersForLocationsOptions,
  PathwayUser,
  Plan,
  PlanAssignment,
} from '@cfa/pathway-fe-shared/xp';

import { isValidPlanId } from '@/utils/isValidPlanId';
import { uniqueBy } from '@/utils/uniqueBy';
import { useRouteMatcher } from '@/utils/url';

import Constants from '@/constants';

import { TeamMemberPlanStatus } from './TeamMemberCard';

interface UseTeamMemberDataProps {
  isOpen: boolean;
  locations: string[];
  plan?: Plan;
  isCompliance?: boolean;
  isTrainTogetherMode?: boolean;
  searchTerm: string;
  filterBy?: string[];
  hidePreviouslyAssigned: boolean;
  selectedTeamMembers: PathwayUser[];
  teamMemberQueryOptions?: Record<string, unknown>;
}

const filterForAllNameProperties = (
  member: PathwayUser,
  searchTerm: string,
) => {
  const searchParts = searchTerm.toLowerCase().split(' ').filter(Boolean);
  const nameFields = [
    member.name?.toLowerCase(),
    member.firstName?.toLowerCase(),
    member.lastName?.toLowerCase(),
    member.prefName?.toLowerCase(),
  ].filter(Boolean);

  return searchParts.every(searchPart =>
    nameFields.some(field => field?.includes(searchPart)),
  );
};

export const useTeamMemberData = ({
  isOpen,
  locations,
  plan,
  isCompliance,
  isTrainTogetherMode,
  searchTerm,
  filterBy,
  hidePreviouslyAssigned,
  selectedTeamMembers,
  teamMemberQueryOptions,
}: UseTeamMemberDataProps) => {
  const routeMatches = useRouteMatcher();

  const isBuildPlansTab = routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.TrainingBuildPath,
  );
  const isMyPlansTab = routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.TrainingMyPlansPath,
  );
  const isReportsPlansTab = routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.ReportsPlansPath,
  );
  const isReportsPlansViewPage = routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.ReportsPlansViewPath,
  );
  const isReportsTeamMembersViewPath = routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.ReportsTeamMembersViewPath,
  );

  const planId = plan?.id ?? '';

  const getTeamMemberPlanStatus = (memberId: string): TeamMemberPlanStatus => {
    if (isTrainTogetherMode) return 'unassigned';
    const assignment = assignments?.find(a => a.userId === memberId);
    if (!assignment || assignment.status === 'UNASSIGNED') {
      return 'unassigned';
    }
    if (assignment.status === 'COMPLETED') {
      return 'completed';
    }
    return 'in-progress';
  };

  // Fetch team members for locations
  const { data: allTeamMembers } = useQuery({
    ...getUsersForLocationsOptions({
      query: { locationNumbers: locations },
    }),
    ...teamMemberQueryOptions,
    enabled: locations.length > 0,
  });

  // Fetch plan assignments
  const { data: planAndAssignments, isSuccess: isSuccessPlanAndAssignment } =
    useQuery({
      ...getPlanAndAssignmentsOptions({ path: { planId } }),
      enabled:
        !!planId &&
        !isMyPlansTab &&
        !isReportsPlansTab &&
        !isReportsPlansViewPage &&
        !isReportsTeamMembersViewPath &&
        (isOpen || !isBuildPlansTab) &&
        isValidPlanId(planId),
      staleTime: 0,
    });

  const { assignments } = planAndAssignments ?? {};

  const teamMembers =
    isCompliance && !isTrainTogetherMode
      ? allTeamMembers?.filter(
          member =>
            member.adId &&
            getTeamMemberPlanStatus(member.adId) === 'unassigned',
        )
      : allTeamMembers;

  // Derive assignedTeamMembers from query results
  const assignedTeamMembers: PlanAssignment[] | undefined =
    isSuccessPlanAndAssignment
      ? assignments?.filter(it => it.status !== 'UNASSIGNED')
      : undefined;

  // Derive nonCompletedTeamMembers from assignedTeamMembers
  const nonCompletedTeamMembers = assignedTeamMembers
    ? assignedTeamMembers
        .filter(
          teamMember =>
            teamMember.status !== Constants.TRAINING_PLANS.COMPLETED,
        )
        .map(member => member.userId)
    : undefined;

  // Derive currentCount from selectedTeamMembers
  const currentCount =
    isTrainTogetherMode && !!nonCompletedTeamMembers?.[0]
      ? selectedTeamMembers.filter(teamMember =>
          nonCompletedTeamMembers.includes(teamMember.adId),
        ).length
      : selectedTeamMembers.length;

  // Derive filtered team members from search term
  const searchFilteredMembers = searchTerm
    ? teamMembers?.filter(member =>
        filterForAllNameProperties(member, searchTerm),
      )
    : teamMembers;

  const alreadyAssignedTeamMembers = searchFilteredMembers
    ?.filter(member =>
      assignedTeamMembers?.some(
        teamMember => teamMember.userId === member.adId,
      ),
    )
    .map(member => member.adId);

  const getCompletionDate = (memberId: string): string | undefined => {
    if (isTrainTogetherMode) return undefined;
    const assignment = assignments?.find(a => a.userId === memberId);
    return assignment?.latestCompletionReport?.completionDate;
  };

  const ids = searchFilteredMembers?.map(o => o.adId);
  const assignedUsers = assignments?.map(user => user.userId);

  const assignedTeamMembersInTrainTogetherMode = uniqueBy(
    searchFilteredMembers?.filter(
      teamMember => assignedUsers?.includes(teamMember.adId) ?? [],
    ),
    'adId',
  );

  const uniqueTeamMembers = isTrainTogetherMode
    ? assignedTeamMembersInTrainTogetherMode
    : searchFilteredMembers?.filter(
        ({ adId }, index) => !ids?.includes(adId, index + 1),
      );

  // Apply location filter
  const locationFilteredTeamMembers = filterBy?.length
    ? uniqueTeamMembers?.filter(member =>
        member.locationList?.some(memberLocation =>
          filterBy.some(location => location === memberLocation),
        ),
      )
    : isTrainTogetherMode
      ? uniqueTeamMembers?.filter(member =>
          assignedTeamMembers?.some(
            teamMember => teamMember.userId === member.adId,
          ),
        )
      : uniqueTeamMembers;

  // Apply "hide previously assigned" filter
  const filteredTeamMembers =
    hidePreviouslyAssigned && !isTrainTogetherMode
      ? locationFilteredTeamMembers?.filter(member => {
          if (!member.adId) {
            return false;
          }
          const status = getTeamMemberPlanStatus(member.adId);
          return status === 'unassigned';
        })
      : locationFilteredTeamMembers;

  // All visible members are selectable (except train-together, which
  // restricts to already-assigned members)
  const selectableTeamMembers = isTrainTogetherMode
    ? filteredTeamMembers?.filter(member =>
        alreadyAssignedTeamMembers?.includes(member.adId),
      )
    : filteredTeamMembers;

  return {
    assignments,
    currentCount,
    filteredTeamMembers,
    selectableTeamMembers,
    alreadyAssignedTeamMembers,
    getTeamMemberPlanStatus,
    getCompletionDate,
  };
};
